'use client';

import { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import CompanyStory from './CompanyStory';
import TeamSection from './TeamSection';
import PartnershipsSection from './PartnershipsSection';
import AwardsSection from './AwardsSection';
import TestimonialsSection from './TestimonialsSection';
import CultureSection from './CultureSection';
import CTASection from './CTASection';

interface Milestone {
  year: string;
  title: string;
  description: string;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  expertise: string[];
  bio: string;
  image: string;
  alt: string;
  certifications: string[];
  linkedin?: string;
}

interface Partnership {
  id: number;
  name: string;
  logo: string;
  alt: string;
  category: string;
}

interface Award {
  id: number;
  title: string;
  organization: string;
  year: string;
  description: string;
  icon: string;
}

interface Testimonial {
  id: number;
  quote: string;
  author: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
}

interface CultureValue {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface CultureImage {
  id: number;
  src: string;
  alt: string;
  caption: string;
}

const AboutInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroData = {
    title: "Building Digital Excellence Since 2015",
    subtitle: "About Wilkam Consultancy",
    description: "We are a team of passionate digital strategists, developers, and SEO experts dedicated to helping businesses achieve measurable online success through the perfect blend of technical excellence and strategic marketing intelligence.",
    heroImage: {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_11f41d650-1768649353082.png",
      alt: "Modern office workspace with diverse team collaborating around large desk with laptops and digital screens"
    }
  };

  const companyStoryData = {
    story: "Wilkam Consultancy was founded in 2015 with a simple yet powerful vision: to bridge the gap between exceptional web development and strategic SEO. Our founders recognized that businesses were often forced to choose between beautiful websites and search-optimized ones. We set out to prove that you don't have to compromise—that technical excellence and marketing intelligence can coexist in perfect harmony. Today, we've helped over 500 businesses transform their digital presence, driving measurable growth through integrated web and SEO solutions.",
    mission: "To empower businesses with integrated digital solutions that combine stunning web design, robust development, and strategic SEO—delivering measurable results that drive sustainable growth and competitive advantage.",
    vision: "To become the most trusted digital growth partner for ambitious businesses worldwide, setting the industry standard for integrated web development and SEO excellence.",
    milestones: [
    {
      year: "2015",
      title: "Company Founded",
      description: "Wilkam Consultancy established with a mission to integrate web development and SEO services under one roof"
    },
    {
      year: "2017",
      title: "Google Partner Status",
      description: "Achieved Google Partner certification, validating our expertise in digital marketing and SEO strategies"
    },
    {
      year: "2019",
      title: "100+ Successful Projects",
      description: "Reached milestone of 100 completed projects with an average client satisfaction rating of 4.8/5"
    },
    {
      year: "2021",
      title: "Team Expansion",
      description: "Grew team to 25+ specialists across development, SEO, design, and strategy departments"
    },
    {
      year: "2023",
      title: "Industry Recognition",
      description: "Won \'Best Integrated Digital Agency\' award and featured in leading industry publications"
    },
    {
      year: "2026",
      title: "500+ Clients Served",
      description: "Celebrating serving over 500 businesses with continued focus on innovation and client success"
    }] as
    Milestone[]
  };

  const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: "Sarah Mitchell",
    role: "Founder & CEO",
    expertise: ["Digital Strategy", "Business Development", "Client Relations"],
    bio: "With 15+ years in digital marketing, Sarah founded Wilkam Consultancy to revolutionize how businesses approach online growth.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a48b2d0-1763294125036.png",
    alt: "Professional woman with shoulder-length brown hair in navy blazer smiling confidently in modern office",
    certifications: ["Google Analytics Certified", "HubSpot Inbound Certified", "MBA - Digital Marketing"],
    linkedin: "https://linkedin.com"
  },
  {
    id: 2,
    name: "Marcus Chen",
    role: "Chief Technology Officer",
    expertise: ["Full-Stack Development", "System Architecture", "DevOps"],
    bio: "Marcus leads our development team with expertise in building scalable, high-performance web applications.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a94acfd5-1763299528067.png",
    alt: "Asian man with short black hair wearing glasses and gray suit in professional office setting",
    certifications: ["AWS Certified Solutions Architect", "React Advanced Certification", "Computer Science MS"],
    linkedin: "https://linkedin.com"
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    role: "Head of SEO Strategy",
    expertise: ["Technical SEO", "Content Strategy", "Analytics"],
    bio: "Emily brings 12 years of SEO expertise, helping clients achieve top rankings and sustainable organic growth.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1faff2fd5-1763296215078.png",
    alt: "Hispanic woman with long dark hair in professional burgundy blazer smiling warmly",
    certifications: ["Google SEO Certification", "Semrush SEO Toolkit Certified", "Content Marketing Institute Certified"],
    linkedin: "https://linkedin.com"
  },
  {
    id: 4,
    name: "David Thompson",
    role: "Creative Director",
    expertise: ["UI/UX Design", "Brand Identity", "Visual Strategy"],
    bio: "David crafts compelling digital experiences that blend aesthetic excellence with conversion-focused design.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ddf71bff-1763295438197.png",
    alt: "Caucasian man with short brown hair and beard in casual blue shirt smiling in creative studio",
    certifications: ["Adobe Certified Expert", "UX Design Specialization", "Brand Strategy Certified"],
    linkedin: "https://linkedin.com"
  },
  {
    id: 5,
    name: "Priya Sharma",
    role: "Lead Developer",
    expertise: ["React/Next.js", "Performance Optimization", "API Integration"],
    bio: "Priya specializes in building lightning-fast, SEO-optimized web applications using modern frameworks.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b581867d-1763301126687.png",
    alt: "Indian woman with long black hair in professional white blouse working on laptop in bright office",
    certifications: ["Next.js Expert", "Web Performance Specialist", "TypeScript Advanced"],
    linkedin: "https://linkedin.com"
  },
  {
    id: 6,
    name: "James Wilson",
    role: "Client Success Manager",
    expertise: ["Project Management", "Client Relations", "Strategic Planning"],
    bio: "James ensures every client receives exceptional service and achieves their digital growth objectives.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1373cd82c-1763294505279.png",
    alt: "African American man with short hair and beard in charcoal suit smiling professionally",
    certifications: ["PMP Certified", "Agile Scrum Master", "Customer Success Certified"],
    linkedin: "https://linkedin.com"
  }];


  const partnerships: Partnership[] = [
  {
    id: 1,
    name: "Google Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_192e5c8b2-1764634732588.png",
    alt: "Google Partner certification badge with colorful G logo",
    category: "Technology Partners"
  },
  {
    id: 2,
    name: "AWS Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1815fd195-1764675850118.png",
    alt: "Amazon Web Services partner logo with orange smile arrow",
    category: "Technology Partners"
  },
  {
    id: 3,
    name: "Semrush Certified",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13ff8f3fe-1767083951460.png",
    alt: "Semrush certification badge with orange and white branding",
    category: "SEO Tools"
  },
  {
    id: 4,
    name: "HubSpot Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11dea1c6e-1764634734422.png",
    alt: "HubSpot partner certification with orange sprocket logo",
    category: "Marketing Platforms"
  },
  {
    id: 5,
    name: "Shopify Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18abb8ff2-1764669429615.png",
    alt: "Shopify partner badge with green shopping bag icon",
    category: "E-commerce Platforms"
  },
  {
    id: 6,
    name: "WordPress VIP",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc1cc553-1765037413221.png",
    alt: "WordPress VIP agency partner logo with blue W icon",
    category: "Technology Partners"
  },
  {
    id: 7,
    name: "Ahrefs Certified",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2d9f56a-1768407993469.png",
    alt: "Ahrefs certification badge with blue and orange branding",
    category: "SEO Tools"
  },
  {
    id: 8,
    name: "Moz Pro Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4dc3f25-1767789780316.png",
    alt: "Moz Pro partner certification with blue robot mascot",
    category: "SEO Tools"
  }];


  const awards: Award[] = [
  {
    id: 1,
    title: "Best Integrated Digital Agency",
    organization: "Digital Marketing Awards",
    year: "2025",
    description: "Recognized for excellence in combining web development and SEO services",
    icon: "TrophyIcon"
  },
  {
    id: 2,
    title: "Top SEO Agency",
    organization: "Search Engine Journal",
    year: "2024",
    description: "Ranked among top 50 SEO agencies globally for technical expertise",
    icon: "StarIcon"
  },
  {
    id: 3,
    title: "Innovation in Web Development",
    organization: "Tech Excellence Awards",
    year: "2024",
    description: "Honored for pioneering approaches to performance optimization",
    icon: "LightBulbIcon"
  },
  {
    id: 4,
    title: "Client Satisfaction Excellence",
    organization: "Business Review Institute",
    year: "2023",
    description: "Achieved 98% client satisfaction rating across all projects",
    icon: "HeartIcon"
  },
  {
    id: 5,
    title: "Best Company Culture",
    organization: "Workplace Excellence Awards",
    year: "2023",
    description: "Recognized for fostering innovative and inclusive work environment",
    icon: "UserGroupIcon"
  },
  {
    id: 6,
    title: "Fast-Growing Agency",
    organization: "Inc. 5000",
    year: "2022",
    description: "Listed among fastest-growing private companies in digital services",
    icon: "ChartBarIcon"
  }];


  const testimonials: Testimonial[] = [
  {
    id: 1,
    quote: "Wilkam Consultancy transformed our online presence completely. Their integrated approach to web development and SEO delivered results beyond our expectations. We saw a 300% increase in organic traffic within six months.",
    author: "Jennifer Martinez",
    role: "Marketing Director",
    company: "TechVenture Solutions",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f225624a-1763293838525.png",
    alt: "Professional Hispanic woman with long dark hair in business attire smiling warmly",
    rating: 5
  },
  {
    id: 2,
    quote: "The team's technical expertise is unmatched. They built us a lightning-fast e-commerce platform that ranks #1 for our primary keywords. Our conversion rate doubled, and revenue increased by 250%.",
    author: "Michael Chen",
    role: "CEO",
    company: "Urban Lifestyle Co.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd81bcbd-1763299899173.png",
    alt: "Asian businessman in navy suit with confident smile in modern office",
    rating: 5
  },
  {
    id: 3,
    quote: "What sets Wilkam Consultancy apart is their strategic thinking. They don't just execute—they partner with you to understand your business goals and craft solutions that drive real growth. Highly recommended!",
    author: "Sarah Johnson",
    role: "Founder",
    company: "GreenEarth Organics",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0e8b7ad-1768047041703.png",
    alt: "Caucasian woman with blonde hair in casual green sweater smiling outdoors",
    rating: 5
  }];


  const cultureValues: CultureValue[] = [
  {
    id: 1,
    title: "Innovation First",
    description: "We embrace cutting-edge technologies and creative problem-solving to deliver exceptional results",
    icon: "💡"
  },
  {
    id: 2,
    title: "Client Success",
    description: "Your growth is our mission. We measure our success by the results we deliver for you",
    icon: "🎯"
  },
  {
    id: 3,
    title: "Transparency",
    description: "Open communication, honest feedback, and clear reporting in everything we do",
    icon: "🔍"
  },
  {
    id: 4,
    title: "Continuous Learning",
    description: "We invest in our team's growth to stay ahead of industry trends and best practices",
    icon: "📚"
  }];


  const cultureImages: CultureImage[] = [
  {
    id: 1,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_139d45c67-1766844396733.png",
    alt: "Diverse team of professionals collaborating around conference table with laptops and documents",
    caption: "Collaborative team meetings drive innovation"
  },
  {
    id: 2,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_11070b3c3-1766933113460.png",
    alt: "Modern open office space with natural lighting and employees working at standing desks",
    caption: "Our inspiring workspace fosters creativity"
  },
  {
    id: 3,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_16465536a-1768369913673.png",
    alt: "Team members celebrating success with high-fives in bright office environment",
    caption: "Celebrating wins together as one team"
  },
  {
    id: 4,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_14d3ce1dd-1766355188599.png",
    alt: "Employees participating in training workshop with presenter at whiteboard",
    caption: "Continuous learning and development"
  },
  {
    id: 5,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1485a7aee-1768389862152.png",
    alt: "Casual team lunch gathering with diverse group sharing meal and conversation",
    caption: "Building connections beyond work"
  },
  {
    id: 6,
    src: "https://images.unsplash.com/photo-1690192079218-fe6dce1486c0",
    alt: "Team members brainstorming ideas with sticky notes on glass wall",
    caption: "Innovation through collaboration"
  }];


  const ctaData = {
    title: "Ready to Transform Your Digital Presence?",
    description: "Join hundreds of businesses that trust Wilkam Consultancy to drive their online growth. Let\'s discuss how we can help you achieve your digital goals.",
    primaryCTA: {
      text: "Get Free SEO Audit",
      href: "/free-seo-audit"
    },
    secondaryCTA: {
      text: "Schedule Consultation",
      href: "/contact"
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/about/components/AboutInteractive.tsx:421:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="421" data-component-end-line="429" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/about/components/AboutInteractive.tsx:422:8" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="422" data-component-end-line="428" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-20%22%7D" className="container mx-auto px-4 py-20">
          <div data-component-id="src/app/about/components/AboutInteractive.tsx:423:10" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="423" data-component-end-line="427" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/about/components/AboutInteractive.tsx:424:12" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="424" data-component-end-line="424" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" className="h-96 bg-muted rounded-xl" />
            <div data-component-id="src/app/about/components/AboutInteractive.tsx:425:12" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="425" data-component-end-line="425" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" className="h-64 bg-muted rounded-xl" />
            <div data-component-id="src/app/about/components/AboutInteractive.tsx:426:12" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="426" data-component-end-line="426" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" className="h-64 bg-muted rounded-xl" />
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/about/components/AboutInteractive.tsx:434:4" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="434" data-component-end-line="443" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <HeroSection data-component-id="src/app/about/components/AboutInteractive.tsx:435:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="435" data-component-end-line="435" data-component-file="AboutInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...heroData} />
      <CompanyStory data-component-id="src/app/about/components/AboutInteractive.tsx:436:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="436" data-component-end-line="436" data-component-file="AboutInteractive.tsx" data-component-name="CompanyStory" data-component-content="%7B%22elementName%22%3A%22CompanyStory%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...companyStoryData} />
      <TeamSection data-component-id="src/app/about/components/AboutInteractive.tsx:437:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="437" data-component-end-line="437" data-component-file="AboutInteractive.tsx" data-component-name="TeamSection" data-component-content="%7B%22elementName%22%3A%22TeamSection%22%7D" teamMembers={teamMembers} />
      <PartnershipsSection data-component-id="src/app/about/components/AboutInteractive.tsx:438:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="438" data-component-end-line="438" data-component-file="AboutInteractive.tsx" data-component-name="PartnershipsSection" data-component-content="%7B%22elementName%22%3A%22PartnershipsSection%22%7D" partnerships={partnerships} />
      <AwardsSection data-component-id="src/app/about/components/AboutInteractive.tsx:439:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="439" data-component-end-line="439" data-component-file="AboutInteractive.tsx" data-component-name="AwardsSection" data-component-content="%7B%22elementName%22%3A%22AwardsSection%22%7D" awards={awards} />
      <TestimonialsSection data-component-id="src/app/about/components/AboutInteractive.tsx:440:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="440" data-component-end-line="440" data-component-file="AboutInteractive.tsx" data-component-name="TestimonialsSection" data-component-content="%7B%22elementName%22%3A%22TestimonialsSection%22%7D" testimonials={testimonials} />
      <CultureSection data-component-id="src/app/about/components/AboutInteractive.tsx:441:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="441" data-component-end-line="441" data-component-file="AboutInteractive.tsx" data-component-name="CultureSection" data-component-content="%7B%22elementName%22%3A%22CultureSection%22%7D" values={cultureValues} images={cultureImages} />
      <CTASection data-component-id="src/app/about/components/AboutInteractive.tsx:442:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="442" data-component-end-line="442" data-component-file="AboutInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...ctaData} />
    </div>);

};

export default AboutInteractive;